import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  TableChart as TableIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';

interface FileViewerProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileType: string;
  fileSize?: number;
}

interface ParsedData {
  headers: string[];
  rows: any[][];
  totalRows: number;
}

const FileViewer: React.FC<FileViewerProps> = ({
  open,
  onClose,
  fileUrl,
  fileName,
  fileType,
  fileSize,
}) => {
  const [data, setData] = useState<ParsedData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<any[][]>([]);
  const [rowsPerPage] = useState(50);

  // Parse file data when component opens
  useEffect(() => {
    if (open && fileUrl) {
      parseFile();
    }
  }, [open, fileUrl]);

  // Filter data when search term changes
  useEffect(() => {
    if (data && searchTerm) {
      const filtered = data.rows.filter(row =>
        row.some(cell => 
          String(cell).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredData(filtered);
    } else if (data) {
      setFilteredData(data.rows);
    }
  }, [data, searchTerm]);

  const parseFile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(fileUrl);
      const arrayBuffer = await response.arrayBuffer();

      if (fileType.includes('csv') || fileName.toLowerCase().endsWith('.csv')) {
        // Parse CSV
        const text = new TextDecoder().decode(arrayBuffer);
        try {
          const parseConfig: Papa.ParseConfig = {
            complete: (results: Papa.ParseResult<any>) => {
              if (results.errors.length > 0) {
                setError('Error parsing CSV: ' + results.errors[0].message);
              } else {
                const rows = results.data as any[][];
                const headers = rows[0] || [];
                const dataRows = rows.slice(1);
                
                setData({
                  headers,
                  rows: dataRows,
                  totalRows: dataRows.length
                });
                setFilteredData(dataRows);
              }
              setLoading(false);
            },
            skipEmptyLines: true,
            header: false
          };
          
          Papa.parse(text, parseConfig);
        } catch (error) {
          setError('Error parsing CSV: ' + (error as Error).message);
          setLoading(false);
        }
      } else if (
        fileType.includes('sheet') || 
        fileType.includes('excel') ||
        fileName.toLowerCase().match(/\.(xlsx|xls)$/)
      ) {
        // Parse Excel
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          setError('Excel file appears to be empty');
          setLoading(false);
          return;
        }

        const rows = jsonData as any[][];
        const headers = rows[0] || [];
        const dataRows = rows.slice(1);

        setData({
          headers,
          rows: dataRows,
          totalRows: dataRows.length
        });
        setFilteredData(dataRows);
        setLoading(false);
      } else {
        setError('Unsupported file type for preview');
        setLoading(false);
      }
    } catch (error) {
      setError('Error loading file: ' + (error as Error).message);
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = () => {
    if (fileType.includes('csv') || fileName.toLowerCase().endsWith('.csv')) {
      return <TableIcon />;
    }
    return <DocumentIcon />;
  };

  const getFileTypeLabel = () => {
    if (fileType.includes('csv') || fileName.toLowerCase().endsWith('.csv')) {
      return 'CSV File';
    }
    if (fileType.includes('sheet') || fileType.includes('excel')) {
      return 'Excel Spreadsheet';
    }
    return 'Data File';
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          maxHeight: '90vh',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                color: 'white',
              }}>
                {getFileIcon()}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {fileName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={getFileTypeLabel()}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {fileSize && (
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(fileSize)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                background: 'rgba(0,0,0,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: 'rgba(255, 255, 255, 0.6)' }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 8,
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography variant="body1">
                Loading file data...
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            </Box>
          ) : data ? (
            <Box sx={{ p: 3 }}>
              {/* Search and Stats */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                gap: 2
              }}>
                <TextField
                  size="small"
                  placeholder="Search in data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {filteredData.length} of {data.totalRows} rows
                  {searchTerm && ` (filtered)`}
                </Typography>
              </Box>

              {/* Data Table */}
              <TableContainer 
                component={Paper} 
                sx={{ 
                  maxHeight: 400,
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {data.headers.map((header, index) => (
                        <TableCell 
                          key={index}
                          sx={{ 
                            fontWeight: 'bold',
                            background: 'linear-gradient(45deg, #f5f5f5 30%, #e0e0e0 90%)',
                            borderBottom: '2px solid #1976d2'
                          }}
                        >
                          {header || `Column ${index + 1}`}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentData.map((row, rowIndex) => (
                      <TableRow 
                        key={rowIndex}
                        hover
                        sx={{ 
                          '&:nth-of-type(odd)': { 
                            backgroundColor: 'rgba(0, 0, 0, 0.02)' 
                          }
                        }}
                      >
                        {row.map((cell, cellIndex) => (
                          <TableCell key={cellIndex}>
                            {String(cell || '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2 
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Download
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default FileViewer;
